import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Search, Copy, Check, Mail, Loader2, ShieldCheck, FileText,
  CheckCircle2, ChevronRight,
} from "lucide-react";
import { Loading } from "../components/kit";

// Full-width application inbox — master-detail. Left: every email that landed at the candidate's
// managed applying address, category-coded. Right: the selected email, with a Message⇄Application
// toggle so the linked application (stepper + what-we-submitted receipt) sits beside its mail.

// Only the states our managed inbox actually produces. No invented pipeline buckets.
const CATS = {
  all:          { label: "All",          color: "#0F172A" },
  unread:       { label: "Unread",       color: "#0025D8" },
  verification: { label: "Verification", color: "#E08A00" },
  applied:      { label: "Confirmed",    color: "#0FA968" },
  other:        { label: "Other",        color: "#64748B" },
};
const SRC = { profile: ["Profile", "#0025D8"], bank: ["Answer bank", "#7C3AED"], resume: ["Résumé", "#0FA968"], ai: ["AI", "#DB2777"], decline: ["Declined", "#64748B"], default_no: ["Default", "#64748B"] };
const STAGE_STEPS = ["preparing", "ready_to_review", "submitted", "confirmed"];
const STAGE_LABEL = { preparing: "Preparing", ready_to_review: "Ready to review", submitted: "Submitted", confirmed: "Confirmed" };

const AV_COLORS = ["#E8A400", "#0075BE", "#B4131E", "#0B7D3E", "#7C3AED", "#0D9488", "#2563EB", "#DB2777"];
const avatarColor = (name) => { let h = 0; for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0; return AV_COLORS[h % AV_COLORS.length]; };
const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "now";
  const m = Math.floor(s / 60); if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24); if (d < 7) return `${d}d`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};
const fmtFull = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "");
const tint = (hex, a = "20") => hex + a;

function CategoryPill({ cat, small }) {
  const c = CATS[cat] || CATS.other;
  return (
    <span className="inline-flex items-center gap-1.5 font-semibold rounded-full whitespace-nowrap"
      style={{ color: c.color, background: tint(c.color, "18"), fontSize: small ? 10.5 : 11.5, padding: small ? "2px 8px" : "3px 10px" }}>
      <span className="rounded-full" style={{ width: 6, height: 6, background: c.color }} /> {c.label}
    </span>
  );
}

function EmailBody({ emailId }) {
  const [full, setFull] = useState(null);
  useEffect(() => {
    let live = true; setFull(null);
    api.get(`/apply/inbox/${emailId}`).then(({ data }) => live && setFull(data)).catch(() => live && setFull({ text: "(Could not load this message.)" }));
    return () => { live = false; };
  }, [emailId]);
  if (!full) return <div className="flex items-center gap-2 text-sm text-muted-foreground p-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  return (
    <div className="max-w-[720px]">
      <div className="rounded-2xl border border-border overflow-hidden bg-white shadow-sm">
        {full.html
          ? <iframe title="message" sandbox="" srcDoc={full.html} className="w-full bg-white" style={{ height: 460, border: 0 }} />
          : <pre className="p-6 text-sm text-[#0F172A] whitespace-pre-wrap font-sans">{full.text || "(empty)"}</pre>}
      </div>
      <p className="text-[11.5px] text-muted-foreground mt-3 max-w-[720px]">
        A copy of this was forwarded to your real inbox{full.from ? <> — reply to <b className="text-[#0F172A]">{full.from}</b> from there.</> : "."}
      </p>
    </div>
  );
}

function ApplicationPanel({ draftId, onOpenFull }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let live = true; setD(null); setErr(false);
    api.get(`/apply/status/${draftId}`).then(({ data }) => live && setD(data)).catch(() => live && setErr(true));
    return () => { live = false; };
  }, [draftId]);
  if (err) return <div className="text-sm text-muted-foreground p-6">Couldn't load this application.</div>;
  if (!d) return <div className="flex items-center gap-2 text-sm text-muted-foreground p-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading application…</div>;
  const fs = d.submitted_fields || {};
  const answers = (d.answers || []).filter((a) => a && a.question);
  const cur = d.stage === "confirmed" ? 3 : d.stage === "submitted" ? 2 : d.stage === "ready_to_review" || d.stage === "action_needed" ? 1 : 0;
  const fields = [["Name", `${fs.first_name || ""} ${fs.last_name || ""}`.trim()], ["Email", fs.email], ["Phone", fs.phone], ["LinkedIn", fs.linkedin], ["GitHub", fs.github], ["Portfolio", fs.portfolio]].filter(([, v]) => v);
  return (
    <div className="max-w-[720px]">
      {d.stage === "confirmed" && (
        <div className="flex items-center gap-2 text-sm font-medium rounded-xl px-3.5 py-2.5 mb-4" style={{ color: "#0FA968", background: tint("#0FA968", "16") }}>
          <CheckCircle2 className="w-4 h-4" /> {d.company} confirmed they received your application.
        </div>
      )}
      {/* stepper */}
      <div className="flex items-center mb-5">
        {STAGE_STEPS.map((s, i) => {
          const done = i < cur, isCur = i === cur;
          return (
            <React.Fragment key={s}>
              <div className="flex items-center gap-2">
                <span className="grid place-items-center rounded-full text-[11px] font-semibold flex-shrink-0"
                  style={{ width: 22, height: 22, background: done ? "#0FA968" : isCur ? "#0025D8" : "#E5E9F2", color: done || isCur ? "#fff" : "#6B7690", boxShadow: isCur ? "0 0 0 4px #EAEEFF" : "none" }}>
                  {done ? "✓" : i + 1}
                </span>
                <span className="text-[12px] font-medium" style={{ color: done || isCur ? "#0F172A" : "#98A2B8" }}>{STAGE_LABEL[s]}</span>
              </div>
              {i < STAGE_STEPS.length - 1 && <span className="flex-1 h-0.5 mx-2 rounded" style={{ background: done ? "#0FA968" : "#E5E9F2" }} />}
            </React.Fragment>
          );
        })}
      </div>
      {/* receipt */}
      <div className="rounded-2xl border border-border bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">
          <ShieldCheck className="w-4 h-4 text-[#0025D8]" /> What we submitted
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0.5">
          {fields.map(([k, v]) => (
            <div key={k} className="flex justify-between gap-3 py-2 border-b border-border/60 text-sm">
              <span className="text-muted-foreground">{k}</span><span className="text-[#0F172A] font-semibold truncate">{v}</span>
            </div>
          ))}
        </div>
        {fs.resume_filename && (
          <div className="inline-flex items-center gap-2 mt-3.5 px-3 py-2 rounded-lg border border-border bg-muted/40 text-sm font-semibold">
            <FileText className="w-4 h-4 text-[#0025D8]" /> {fs.resume_filename}
            {fs.resume_attached && <span className="text-[10.5px] text-[#0FA968] font-bold tracking-wide">ATTACHED</span>}
          </div>
        )}
      </div>
      {answers.length > 0 && (
        <div className="rounded-2xl border border-border bg-white p-5 shadow-sm mt-3.5">
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">{answers.length} questions answered</div>
          {answers.map((a, i) => {
            const s = SRC[a.source];
            return (
              <div key={a.id || i} className="py-3 border-b border-border/60 last:border-0">
                <div className="flex items-start justify-between gap-3">
                  <span className="text-sm font-semibold text-[#0F172A]">{a.question}</span>
                  {s && <span className="text-[10px] font-bold rounded-full px-2 py-0.5 flex-shrink-0 whitespace-nowrap" style={{ color: s[1], background: tint(s[1], "18") }}>{s[0]}</span>}
                </div>
                <div className="text-[13px] text-muted-foreground mt-1">{a.answer || "—"}</div>
              </div>
            );
          })}
        </div>
      )}
      <button onClick={onOpenFull} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0025D8] hover:underline mt-4">
        Open full application <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function ConnectModal({ open, onClose, forwardTo }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 backdrop-blur-sm p-5" onClick={onClose}>
      <div className="bg-white rounded-3xl border border-border max-w-md w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="w-12 h-12 rounded-2xl grid place-items-center text-2xl mb-3.5" style={{ background: "#EAEEFF" }}>✉️</div>
        <h2 className="text-xl font-bold tracking-tight text-[#0F172A] mb-1.5">Connect your own email</h2>
        <p className="text-[13.5px] text-muted-foreground mb-4 leading-relaxed">
          See employer replies from your real inbox right here, and we'll auto-fill verification codes for you.
          We read <b>only</b> job-related mail — nothing else is stored.
        </p>
        {[["Connect Gmail", forwardTo || "your Google account", "#EA4335", "G"], ["Connect Outlook", "Personal or work account", "#0A66C2", "O"]].map(([t, sub, col, ltr]) => (
          <button key={t} onClick={() => toast("Email connector is coming soon — you'll be able to link Gmail/Outlook here.")}
            className="w-full flex items-center gap-3 px-3.5 py-3 rounded-xl border border-border bg-muted/30 mb-2.5 hover:border-[#0025D8] hover:bg-[#EAEEFF] transition-colors text-left">
            <span className="w-8 h-8 rounded-lg grid place-items-center font-extrabold text-white text-sm" style={{ background: col }}>{ltr}</span>
            <span><span className="block text-sm font-semibold text-[#0F172A]">{t}</span><span className="text-[12px] text-muted-foreground">{sub}</span></span>
            <ChevronRight className="w-4 h-4 text-[#98A2B8] ml-auto" />
          </button>
        ))}
        <p className="flex items-start gap-2 text-[11.5px] text-[#98A2B8] mt-3.5 leading-relaxed">
          <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          Secured through our verified email connector. Revoke anytime in Settings. Until you connect, everything routes through your VetraHub address.
        </p>
      </div>
    </div>
  );
}

export default function ApplyInbox() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [filter, setFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [view, setView] = useState("msg");
  const [q, setQ] = useState("");
  const [copied, setCopied] = useState(false);
  const [connect, setConnect] = useState(false);
  const rowsRef = useRef(null);

  // ?open=<email_id> — the hub's inbox peek links straight to a message.
  const wanted = params.get("open");
  useEffect(() => {
    api.get("/apply/inbox").then(({ data }) => {
      setData(data);
      const pick = (wanted && data.emails?.some((e) => e.email_id === wanted)) ? wanted : data.emails?.[0]?.email_id;
      if (pick) setSelectedId(pick);
      if (wanted) { params.delete("open"); setParams(params, { replace: true }); }
    }).catch(() => setErr(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emails = data?.emails || [];
  const visible = emails.filter((e) => {
    if (filter === "unread") { if (e.read) return false; }
    else if (filter !== "all" && (e.category || "other") !== filter) return false;
    if (q) { const t = `${e.from} ${e.subject} ${e.company || ""}`.toLowerCase(); if (!t.includes(q)) return false; }
    return true;
  });
  const selected = emails.find((e) => e.email_id === selectedId) || visible[0];

  const select = useCallback((id) => {
    setSelectedId(id); setView("msg");
    setData((d) => d && ({ ...d, emails: d.emails.map((e) => e.email_id === id ? { ...e, read: true } : e) }));
  }, []);

  // Opening a message marks it read, which is easy to do by accident while scanning — put it back.
  const markUnread = useCallback(async (id) => {
    setData((d) => d && ({ ...d, emails: d.emails.map((e) => e.email_id === id ? { ...e, read: false } : e) }));
    try { await api.post(`/apply/inbox/${id}/unread`); }
    catch { setData((d) => d && ({ ...d, emails: d.emails.map((e) => e.email_id === id ? { ...e, read: true } : e) })); }
  }, []);

  // Snapshot through the updater rather than closing over `emails` — that list is derived from
  // `data` on every render, so depending on it would rebuild this callback each time.
  const markAllRead = useCallback(async () => {
    let prev = null;
    setData((d) => { if (!d) return d; prev = d.emails; return { ...d, emails: d.emails.map((e) => ({ ...e, read: true })) }; });
    try { await api.post("/apply/inbox/read-all"); }
    catch { setData((d) => (d && prev ? { ...d, emails: prev } : d)); }
  }, []);

  // keyboard j/k
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === "INPUT") return;
      const i = visible.findIndex((x) => x.email_id === (selected?.email_id));
      if (e.key === "j" && i < visible.length - 1) select(visible[i + 1].email_id);
      if (e.key === "k" && i > 0) select(visible[i - 1].email_id);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible, selected, select]);

  if (err) return <div className="max-w-2xl mx-auto px-4 py-16 text-center text-muted-foreground">Could not load your inbox.</div>;
  if (!data) return <Loading />;

  const counts = emails.reduce((m, e) => { const k = e.category || "other"; m[k] = (m[k] || 0) + 1; return m; }, {});
  const unreadCount = emails.filter((e) => !e.read).length;
  const chips = ["all", ...(unreadCount ? ["unread"] : []),
                 ...Object.keys(CATS).filter((k) => k !== "all" && counts[k])];
  const copy = () => { navigator.clipboard?.writeText(data.address || ""); setCopied(true); setTimeout(() => setCopied(false), 1000); };

  return (
    <div className="h-screen flex flex-col bg-[#F4F6FB]">
      {/* top bar */}
      <header className="flex items-center gap-3 px-4 sm:px-5 py-2.5 bg-white border-b border-border flex-shrink-0">
        <button onClick={() => navigate("/apply/tracker")} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-[#0F172A] pr-1">
          <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Applications</span>
        </button>
        <div className="flex items-center gap-2 font-bold tracking-tight text-[15px] text-[#0F172A]">
          <span className="w-6 h-6 rounded-lg grid place-items-center text-white text-sm font-extrabold" style={{ background: "linear-gradient(135deg,#0025D8,#4B63FF)" }}>V</span>Inbox
        </div>
        {data.address && (
          <div className="hidden md:flex items-center gap-2 ml-1 pl-2 pr-2 py-1 rounded-full bg-muted/50 border border-border text-[12.5px]">
            <b className="text-[#0F172A] font-semibold">{data.address}</b>
            {data.forward_to && <span className="text-muted-foreground">→ {data.forward_to}</span>}
            <button onClick={copy} className="grid place-items-center w-5 h-5 rounded text-muted-foreground hover:text-[#0F172A]" title="Copy address">
              {copied ? <Check className="w-3.5 h-3.5 text-[#0FA968]" /> : <Copy className="w-3.5 h-3.5" />}
            </button>
          </div>
        )}
        <div className="flex-1" />
        <div className="relative w-[220px] max-w-[34vw] hidden sm:block">
          <Search className="w-4 h-4 text-[#98A2B8] absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value.toLowerCase().trim())} placeholder="Search…"
            className="w-full pl-8 pr-3 py-2 rounded-lg border border-border bg-muted/40 text-[13px] outline-none focus:ring-2 focus:ring-[#0025D8]/30" />
        </div>
        <button onClick={() => setConnect(true)}
          className="inline-flex items-center gap-2 text-[13px] font-semibold px-3 py-2 rounded-lg border transition-colors"
          style={{ borderColor: "#0025D8", color: "#0025D8", background: "#EAEEFF" }}>
          <Mail className="w-4 h-4" /> <span className="hidden sm:inline">Connect your own email</span><span className="sm:hidden">Connect</span>
        </button>
      </header>

      {/* category rail */}
      <nav className="flex items-center gap-2 px-4 sm:px-5 py-2.5 bg-white border-b border-border flex-shrink-0 overflow-x-auto" style={{ scrollbarWidth: "none" }}>
        {chips.map((k) => {
          const c = CATS[k], on = filter === k;
          return (
            <button key={k} onClick={() => setFilter(k)} aria-selected={on}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[12.5px] font-semibold whitespace-nowrap border transition-colors"
              style={on ? { background: "#0F172A", borderColor: "#0F172A", color: "#fff" } : { background: "#FBFCFE", borderColor: "#E5E9F2", color: "#3D4761" }}>
              {k !== "all" && <span className="rounded-full" style={{ width: 7, height: 7, background: c.color }} />}
              {c.label}<span className="text-[11px]" style={{ color: on ? "rgba(255,255,255,.7)" : "#6B7690" }}>{k === "all" ? emails.length : counts[k]}</span>
            </button>
          );
        })}
      </nav>

      {/* split */}
      <div className="flex-1 grid min-h-0" style={{ gridTemplateColumns: "minmax(320px,404px) 1fr" }}>
        {/* list */}
        <aside className="border-r border-border overflow-y-auto bg-white min-h-0" ref={rowsRef}>
          <div className="flex items-center justify-between px-4 pt-3 pb-2 sticky top-0 bg-white z-10">
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{visible.length} message{visible.length !== 1 ? "s" : ""}</span>
            <span className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button type="button" onClick={markAllRead}
                  className="text-[11px] font-semibold text-[#0025D8] hover:underline whitespace-nowrap">
                  Mark all read
                </button>
              )}
              <span className="text-[11px] text-[#98A2B8] hidden lg:flex items-center gap-1">
                <kbd className="border border-border rounded px-1.5 py-px text-[10.5px] bg-muted/50">J</kbd>
                <kbd className="border border-border rounded px-1.5 py-px text-[10.5px] bg-muted/50">K</kbd> to move
              </span>
            </span>
          </div>
          {visible.length === 0 ? (
            <div className="px-4 py-16 text-center text-sm text-muted-foreground">No messages match.</div>
          ) : visible.map((e) => {
            const on = selected?.email_id === e.email_id, c = CATS[e.category] || CATS.other;
            return (
              <button key={e.email_id} onClick={() => select(e.email_id)} aria-selected={on}
                className="relative w-full grid gap-3 px-4 py-3 border-b border-border/60 text-left transition-colors hover:bg-muted/30"
                style={{ gridTemplateColumns: "38px 1fr", background: on ? "#EAEEFF" : "transparent" }}>
                <span className="absolute left-0 top-2.5 bottom-2.5 rounded-r" style={{ width: 3, background: c.color }} />
                {on && <span className="absolute inset-y-0 left-0 w-0.5 bg-[#0025D8]" />}
                <span className="w-[38px] h-[38px] rounded-[10px] grid place-items-center text-white font-bold text-sm" style={{ background: avatarColor(e.company || e.from) }}>
                  {(e.company || e.from || "?")[0].toUpperCase()}
                </span>
                <span className="min-w-0">
                  <span className="flex items-baseline gap-2">
                    {!e.read && <span className="w-1.5 h-1.5 rounded-full bg-[#0025D8] flex-shrink-0" />}
                    <span className={`text-[13.5px] text-[#0F172A] truncate ${e.read ? "font-semibold" : "font-bold"}`}>{e.company || e.from}</span>
                    <span className="ml-auto text-[11.5px] text-[#98A2B8] flex-shrink-0" style={{ fontVariantNumeric: "tabular-nums" }}>{timeAgo(e.received_at)}</span>
                  </span>
                  <span className={`block text-[13px] truncate mt-0.5 ${e.read ? "text-[#3D4761]" : "text-[#0F172A]"}`}>{e.subject}</span>
                  {e.snippet && <span className="block text-[12px] text-muted-foreground truncate mt-0.5">{e.snippet}</span>}
                  <span className="flex mt-1.5"><CategoryPill cat={e.category || "other"} small /></span>
                </span>
              </button>
            );
          })}
        </aside>

        {/* detail */}
        <section className="flex flex-col min-h-0 bg-[#FBFCFE]">
          {!selected ? (
            <div className="flex-1 grid place-items-center text-sm text-muted-foreground">Select a message.</div>
          ) : (
            <>
              <div className="px-5 sm:px-7 pt-5 flex-shrink-0">
                <h1 className="text-[19px] leading-snug font-bold tracking-tight text-[#0F172A] mb-2.5" style={{ textWrap: "balance", maxWidth: "66ch" }}>{selected.subject}</h1>
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-1 rounded-full border border-border bg-white text-[#3D4761]">
                    <span className="w-3.5 h-3.5 rounded grid place-items-center text-white text-[9px] font-extrabold" style={{ background: avatarColor(selected.company || selected.from) }}>{(selected.company || "?")[0]}</span>
                    {selected.company || "Unknown"}
                  </span>
                  <CategoryPill cat={selected.category || "other"} />
                  {/* WHICH of the candidate's managed addresses this employer wrote to. They all feed
                      this one inbox, but an employer only ever knows the one they were given — and a
                      reply from a different address would look like it came from a stranger. */}
                  {selected.address && (
                    <span title={`This employer has you as ${selected.address}`}
                      className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-1 rounded-full border border-border bg-white text-[#3D4761] max-w-full">
                      <Mail className="w-3 h-3 flex-shrink-0" style={{ color: "#0025D8" }} />
                      <span className="truncate">{selected.address}</span>
                      {data.address && selected.address !== data.address && (
                        <span className="text-[10px] font-bold px-1.5 py-px rounded-full flex-shrink-0"
                          style={{ background: "#0025D815", color: "#0025D8" }}>alias</span>
                      )}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-[12.5px] text-muted-foreground pb-3.5 border-b border-border">
                  <b className="text-[#0F172A] font-semibold">{selected.from}</b>
                  <button type="button" onClick={() => markUnread(selected.email_id)}
                    className="text-[11.5px] font-semibold text-[#0025D8] hover:underline">
                    Mark unread
                  </button>
                  <span className="ml-auto" style={{ fontVariantNumeric: "tabular-nums" }}>{fmtFull(selected.received_at)}</span>
                </div>
                {selected.draft_id && (
                  <div className="inline-flex bg-muted/40 border border-border rounded-[10px] p-0.5 gap-0.5 mt-3.5">
                    {[["msg", "Message"], ["app", "Application"]].map(([v, l]) => (
                      <button key={v} onClick={() => setView(v)} aria-selected={view === v}
                        className="px-3.5 py-1.5 rounded-[7px] text-[12.5px] font-semibold transition-colors"
                        style={view === v ? { background: "#fff", color: "#0F172A", boxShadow: "0 1px 2px rgba(16,23,41,.08)" } : { color: "#6B7690" }}>{l}</button>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex-1 overflow-y-auto px-5 sm:px-7 py-4 min-h-0">
                {view === "app" && selected.draft_id
                  ? <ApplicationPanel draftId={selected.draft_id} onOpenFull={() => navigate(`/apply/status/${selected.draft_id}`)} />
                  : <EmailBody emailId={selected.email_id} />}
              </div>
            </>
          )}
        </section>
      </div>

      <ConnectModal open={connect} onClose={() => setConnect(false)} forwardTo={data.forward_to} />
    </div>
  );
}
